/**
 * Newcomer navigation: one entry, one progression, today's bar at the end
 * (navigation-design 2026-10-25, BUILD-DECISIONS 2026-10-26, contract
 * build/API.md v1).
 *
 * The server decides every rule (the stage, the bar, the Altar's tabs, the
 * links, the dots, when money shows) and sends it as `nav` on start-session/,
 * care-package/open/, room/recommend/ and nav/. The app keeps only what it
 * must: the device's door, decided once on the first visit, and the bar's
 * collapse inside a reading.
 */
import { frontDoorSub } from '@/utils/mystic-host'

/** No `nav` (an old payload, a failed call): today's bar. Nobody loses a tab. */
const TODAY = Object.freeze({
  signedIn: true, staged: false, stage: 3, door: '', mystic: null,
  bar: [{ key: 'altar', label: 'Altar' }, { key: 'community', label: 'Community' }, { key: 'quest', label: 'Quest' }],
  altarTabs: ['altar', 'mirror', 'history'], altarFirstTab: 'altar',
  communityOpen: true, caratsVisible: true, pricesVisible: true, balanceInMenu: true,
  readings: { count: 0, threshold: 3 }, unlock: null, lineDue: null, stageEvent: null, links: {}, membersOnly: ['messages'],
  rooms: null, heroesDue: [], inReading: false, mirrorSections: null, skyGift: null, freeUntilCommunity: null,
})

const SIGNED_OUT = Object.freeze({
  ...TODAY, signedIn: false, staged: true, stage: 0, bar: [], altarTabs: [], communityOpen: false,
  caratsVisible: false, pricesVisible: false, balanceInMenu: false,
})

// The Quest journey's slot 1 grows Journal → Mirror → Altar (the Mirror's icon is drawn in StagedBar).
const ICONS = { journal: 'fa-book-open', mirror: '', altar: 'fa-eye', community: 'fa-comments', quest: 'fa-scroll', studio: 'fa-wand-magic-sparkles' }
// The server's Altar tab names → MysticAltar's keys.
const ALTAR_KEYS = { home: 'altar', mirror: 'mirror', journal: 'history' }

/** The server's `nav` as the app's one shape. */
export function readNav (n, { signedIn } = {}) {
  if (!n || typeof n !== 'object') return signedIn === false ? { ...SIGNED_OUT } : { ...TODAY }
  const m = n.mystic && typeof n.mystic === 'object' ? n.mystic : null
  const readings = n.readings && typeof n.readings === 'object' ? n.readings : {}
  return {
    signedIn: n.signed_in !== false,
    staged: !!n.staged,
    stage: Number.isInteger(n.stage) ? n.stage : 3,
    door: n.door || '',
    mystic: m ? { sub: m.sub || '', name: m.name || '', label: m.label || m.name || '', portrait: m.portrait || '',
      home: m.home || 'room' } : null,
    bar: Array.isArray(n.bar) ? n.bar.map((i) => ({ ...i })) : [],
    altarTabs: Array.isArray(n.altar_tabs) ? n.altar_tabs.map((t) => ALTAR_KEYS[t] || t) : [],
    altarFirstTab: ALTAR_KEYS[n.altar_first_tab] || 'altar',
    communityOpen: !!n.community_open,
    caratsVisible: !!n.carats_visible,
    pricesVisible: !!n.prices_visible,
    balanceInMenu: !!n.balance_in_menu,
    readings: { count: Number(readings.count) || 0, threshold: Number(readings.threshold) || 3 },
    unlock: n.unlock || null,
    lineDue: n.line_due || null,
    stageEvent: n.stage_event || null,
    links: n.links && typeof n.links === 'object' ? n.links : {},
    membersOnly: Array.isArray(n.members_only) ? n.members_only : ['messages'],
    // The Quest journey (API v1 §2): the rooms, the due reveal is `unlock`,
    // the heroes not yet seen, the server's in-reading flag, the sky gift and
    // the free edits before the Community.
    rooms: n.rooms && typeof n.rooms === 'object' ? { ...n.rooms } : null,
    heroesDue: Array.isArray(n.heroes_due) ? n.heroes_due.slice() : [],
    inReading: !!n.in_reading,
    mirrorSections: Array.isArray(n.mirror_sections) ? n.mirror_sections.slice() : null,
    skyGift: n.sky_gift && typeof n.sky_gift === 'object' ? { ...n.sky_gift } : null,
    freeUntilCommunity: n.free_until_community && typeof n.free_until_community === 'object' ? { ...n.free_until_community } : null,
  }
}

/**
 * The bar to draw, or null for none. The items are the server's, in its
 * order; inside a reading the bar collapses to Menu and expands to them.
 */
export function barItems (nav, { inReading = false, expanded = false } = {}) {
  if (!nav || !Array.isArray(nav.bar) || !nav.bar.length) return null
  const items = nav.bar.map((i) => ({
    key: i.key,
    label: i.label || (i.key === 'mystic' ? 'Mystic Aida' : i.key),
    icon: ICONS[i.key] || '',
    portrait: i.key === 'mystic' ? (i.portrait || '') : '',
    sub: i.key === 'mystic' ? (i.sub || (nav.mystic && nav.mystic.sub) || 'iam') : '',
    dot: !!i.dot,
  }))
  return { items, collapsed: !!inReading && !expanded, close: !!inReading && !!expanded }
}

/** Their Mystic's tab is lit while they are on that Mystic's page or room. */
export function mysticTabLit (nav, { currentSub = '', activePanel = '', inGiftRoom = false } = {}) {
  const item = nav && Array.isArray(nav.bar) && nav.bar.find((i) => i.key === 'mystic')
  if (!item) return false
  if (activePanel !== '' && activePanel !== 'session') return false
  if (inGiftRoom) return true
  const sub = item.sub || (nav.mystic && nav.mystic.sub) || ''
  return !!currentSub && currentSub === sub
}

/**
 * The Altar's tabs (MysticAltar's keys: altar = Home, history = Journal).
 * A staged newcomer has exactly the server's, none before the Journal is born
 * (quest journey API §2); an empty list is never today's three (prod
 * 2026-10-30: it opened the Moon's rite at First Star). Members, and no word
 * from the server, keep today's three.
 */
export function altarTabs (nav) {
  if (nav && nav.staged) return Array.isArray(nav.altarTabs) ? nav.altarTabs : []
  if (!nav || !nav.altarTabs || !nav.altarTabs.length) return ['altar', 'mirror', 'history']
  return nav.altarTabs
}

/**
 * A new room inside the Altar (recipient polish 2026-11-03, DESIGN §14): the
 * server's quiet dot on the Altar tab (the Mirror, or the Circle inside it)
 * also sits on the Mirror tab inside, until the Mirror is opened. 'mirror' | ''.
 */
export function altarRoomDot (nav) {
  if (!nav || !nav.staged || !Array.isArray(nav.bar)) return ''
  const slot = nav.bar.find((i) => i.key === 'altar')
  return slot && slot.dot && altarTabs(nav).includes('mirror') ? 'mirror' : ''
}

/** The Altar's Home (its rite, its sky) is theirs: The Moon is drawn, or a member. */
export function altarHomeOpen (nav) {
  return altarTabs(nav).includes('altar')
}

/**
 * Where a signed-in visit with no panel in the address lands: a newcomer in
 * their Mystic's room until the Altar has its Home (navigation-design §2,
 * Home arriving with The Moon since the quest journey); then, and for every
 * member, the Altar as today. The Moon drawn while away lands in the room
 * too until its reveal is seen: the reveal grows the bar first, and its "Get
 * started" opens the Altar's rite. 'room' | 'altar'.
 */
export function signedInLanding (nav) {
  if (!nav || !nav.staged) return 'altar'
  const u = nav.unlock
  const moonDue = !!(u && (u.constellation === 'the_moon' || u.key === 'unlock:the_moon'))
  return altarHomeOpen(nav) && !moonDue ? 'altar' : 'room'
}

/** The `nav/seen/` key an Altar open on this tab records, or ''. */
export function altarSeenKey (tab) {
  return tab === 'history' ? 'altar:journal' : (tab === 'mirror' ? 'altar:mirror' : '')
}

/** The Quest tab: a plain dot before the Community opens, the carats after. */
export function questBadge (nav, unclaimed = 0) {
  const item = nav && Array.isArray(nav.bar) ? nav.bar.find((i) => i.key === 'quest') : null
  if (!nav || !nav.caratsVisible) return { dot: !!(item && item.dot), count: 0 }
  return { dot: false, count: Number(unclaimed) || 0 }
}

/** The first URL of a visit decides the door and its Mystic. */
export function detectDoor ({ pathname = '/', search = '', hostname = '' } = {}) {
  const q = new URLSearchParams(search || '')
  const path = pathname || '/'
  if (/^\/g\//.test(path) || q.get('keep') || (q.get('reading') && q.get('inv'))) return { door: 'gift', mystic: '' }
  if (/^\/s\//.test(path)) return { door: 'lesson', mystic: '' }
  // A Mystic's front door (mystic-page 2026-10-29): /m/{sub} or {sub}.mysticaida.ai.
  const front = frontDoorSub({ hostname, pathname: path, search })
  if (front) return { door: 'mystic', mystic: front }
  const m = path.match(/^\/reader\/([^/?#]+)/)
  const reader = q.get('reader') || (m && decodeURIComponent(m[1])) || ''
  if (reader && q.get('from')) return { door: 'lesson', mystic: reader }
  if (reader) return { door: 'mystic', mystic: reader }
  return { door: 'organic', mystic: 'iam' }
}

const DOOR_KEY = 'ma_door'
function safeStorage () {
  try { return window.localStorage } catch (e) { return null }
}

/** The device's door, kept from the first visit. */
export function deviceDoor (storage = safeStorage()) {
  try {
    const raw = storage && storage.getItem(DOOR_KEY)
    if (!raw) return null
    const d = JSON.parse(raw)
    return d && d.door ? { door: d.door, mystic: d.mystic || '' } : null
  } catch (e) { return null }
}

/** Decided once: the first visit's door is kept; later visits don't change it. */
export function rememberDoor (door, storage = safeStorage()) {
  const kept = deviceDoor(storage)
  if (kept) return { ...kept, first: false }
  try { storage && storage.setItem(DOOR_KEY, JSON.stringify({ door: door.door, mystic: door.mystic || '' })) } catch (e) { /* */ }
  return { door: door.door, mystic: door.mystic || '', first: true }
}

/** A gift's Mystic is known once the package opens; fill it in, never change the door. */
export function fillDoorMystic (sub, storage = safeStorage()) {
  const kept = deviceDoor(storage)
  if (!kept || kept.mystic || !sub) return kept
  try { storage.setItem(DOOR_KEY, JSON.stringify({ door: kept.door, mystic: sub })) } catch (e) { /* */ }
  return { door: kept.door, mystic: sub }
}

/** The query/body fields the server reads the door from. */
export function doorParams (storage = safeStorage()) {
  const d = deviceDoor(storage)
  return d ? { door: d.door, door_reader: d.mystic || '' } : {}
}

/**
 * What a deep link does, read from `nav.links` (§6). The server's `show` is
 * the answer; the app never makes a place for one. A first-visit Community
 * link signed out is the organic door.
 */
export function deepLink (name, nav, { firstVisit = false } = {}) {
  const links = (nav && nav.links) || {}
  const entry = links[name] || null
  // Community before it is revealed is never reachable by link (Chris
  // 2026-11-04). `firstVisit` is the page's landing: an in-app move after it
  // ("read a lesson") is not a link. 'home' falls through to their own landing.
  if (name === 'community' && firstVisit && nav &&
      (entry ? entry.show === 'page' : !nav.signedIn)) {
    return { show: nav.signedIn ? 'home' : 'organic' }
  }
  if (!entry || !entry.show) {
    // No word from the server yet: the safe answer for someone signed out of a
    // members-only surface is the sign-in sheet, never a blank panel.
    if (nav && !nav.signedIn && ['altar', 'quest', 'messages', 'studio', 'store'].includes(name)) {
      return { show: 'sign_in', reason: SIGNIN_FALLBACK[name] }
    }
    return { show: 'tab' }
  }
  return { ...entry }
}

/**
 * The server ties this session to an account, but the browser holds no sign-in
 * (a dead token was dropped and the session stayed): the page would show the
 * account's bar and prices under Reveal Myself. A gift's room answers for its
 * own viewer, so its door is left alone.
 */
export function sessionOutlivedSignIn (nav, { token = '', querentId = '', door = '' } = {}) {
  return !!(nav && nav.signed_in === true) && !token && !querentId && door !== 'gift'
}

/**
 * The device's first visit counts for the page's landing only (the URL they
 * arrived on). Every later in-app move (e.g. "read a lesson" writing
 * ?panel=community) is not a first visit, or the organic door would take
 * the page straight back to the room.
 */
export function landingGate (first) {
  let landed = false
  return () => {
    const now = !!first && !landed
    landed = true
    return now
  }
}

const SIGNIN_FALLBACK = {
  altar: 'Start with a reading',
  quest: 'Sign in to see your Quest',
  messages: 'Sign in to read your messages',
  studio: 'Sign in to open your Studio',
  store: 'Sign in first',
}

/** The sign-in sheet's title and line per reason. */
export function signInCopy (reason = '') {
  if (/messages/i.test(reason)) return { title: 'Sign in to read your messages', body: 'Your messages are kept with your place here.' }
  if (/quest/i.test(reason)) return { title: 'Sign in to see your Quest', body: "Your Quest keeps what you've done here and what's next.", secondary: 'Start with a reading' }
  if (/studio/i.test(reason)) return { title: 'Sign in to open your Studio', body: 'The Studio is where a Mystic is made. Quest shows the way there.' }
  if (/reading/i.test(reason)) {
    return { title: 'Sign in to open your Altar',
      body: "Your Altar keeps your readings and your signs. Sign in, or start with a reading: it's kept for you as soon as you have one.",
      secondary: 'Start with a reading' }
  }
  return { title: 'Sign in first', body: reason || 'Sign in to pick up where you left off.' }
}

/**
 * Every newcomer door draws without a sign-in wall: signed out, the draw makes
 * a guest place (`guest-place/`, or the gift's own care-package door), then
 * "Keep this". Only the draw: navigation never makes a place.
 */
export function newcomerDrawsAsGuest ({ signedIn = false, door = '' } = {}) {
  if (signedIn) return false
  return ['organic', 'mystic', 'ask', 'lesson', 'gift'].includes(door || 'organic')
}

/**
 * The Mystic's unlock line with its links (API §5 line_due): "…: meet them in
 * Community. And Quest keeps…" → [{text}, {text, to: 'community'}, …].
 */
export function navLineSegments (text = '', links = []) {
  let segs = [{ text: String(text || '') }]
  for (const l of (Array.isArray(links) ? links : [])) {
    if (!l || !l.words) continue
    const out = []
    let done = false
    for (const seg of segs) {
      const at = !done && !seg.to ? seg.text.indexOf(l.words) : -1
      if (at === -1) { out.push(seg); continue }
      if (at) out.push({ text: seg.text.slice(0, at) })
      out.push({ text: l.words, to: l.to })
      const rest = seg.text.slice(at + l.words.length)
      if (rest) out.push({ text: rest })
      done = true
    }
    segs = out
  }
  return segs
}

/**
 * The portrait menu (top right) per stage, DESIGN §7: none at Stage 0 (no
 * kept reading yet, as in the gift room before the card); Account, Messages
 * and Sign out from Stage 1, a guest place with a kept reading included; the
 * balance joins at the unlock (`balanceInMenu`). Accounts from before the
 * deploy are stage 3 and always have it.
 */
export function portraitMenuShown (nav) {
  if (!nav) return true
  return !nav.staged || Number(nav.stage) >= 1
}
